import type { RuntimeConfigPayload, VerifiedRuntimeConfig } from './protocol';

type RuntimeConfigState = {
  installed: Readonly<RuntimeConfigPayload> | null;
  verifiedIdentity: Readonly<Omit<VerifiedRuntimeConfig, 'payload'>> | null;
};

const createState = (): RuntimeConfigState => ({ installed: null, verifiedIdentity: null });
const testStateKey = '__RWM_RUNTIME_CONFIG_TEST_STATE__';
const testGlobal = globalThis as typeof globalThis & { [testStateKey]?: RuntimeConfigState };
const state = import.meta.env.MODE === 'test'
  ? (testGlobal[testStateKey] ??= createState())
  : createState();

const deepFreeze = <T>(value: T): Readonly<T> => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value as Record<string, unknown>).forEach((nested) => deepFreeze(nested));
    Object.freeze(value);
  }
  return value as Readonly<T>;
};

export const installRuntimeConfig = (verified: VerifiedRuntimeConfig): void => {
  if (state.installed) throw new Error('Runtime configuration is already installed.');
  state.installed = deepFreeze(verified.payload);
  state.verifiedIdentity = Object.freeze({
    documentDigest: verified.documentDigest,
    keyId: verified.keyId,
    expiresAtSeconds: verified.expiresAtSeconds,
  });
};

export const installLocalRuntimeConfig = (payload: RuntimeConfigPayload): void => {
  if (state.installed) throw new Error('Runtime configuration is already installed.');
  state.installed = deepFreeze(payload);
  state.verifiedIdentity = Object.freeze({ documentDigest: 'local:unsigned', keyId: 'local', expiresAtSeconds: 0 });
};

export const getRuntimeConfig = (): Readonly<RuntimeConfigPayload> => {
  if (!state.installed) throw new Error('Runtime configuration is not initialized.');
  return state.installed;
};

export const getRuntimeConfigIdentity = () => {
  if (!state.verifiedIdentity) throw new Error('Runtime configuration is not initialized.');
  return state.verifiedIdentity;
};

export const resetRuntimeConfigForTests = (): void => {
  state.installed = null;
  state.verifiedIdentity = null;
};
